import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useLinkLikeClick } from '../../../../../hooks'
import { PageEmptyState } from '../../../../PageEmptyState'
import { getViewingProfileLink } from '../../../../RentalPortalViewing'
import { Table } from '../../../../Table'
import { TablePagination } from '../../../../TablePagination'
import { useListingProfileContext } from '../RentalPortalListingProfile.hooks'
import { RentalPortalListingProfileTabLoadError } from '../RentalPortalListingProfileTabLoadError'

import { ListingProfileViewingsRow } from './ListingProfileViewingsRow'
import {
  LISTING_PROFILE_VIEWINGS_SKELETON_ROWS,
  LISTING_VIEWING_COLUMNS_CONFIG,
  QA_NAME_LISTING_PROFILE_VIEWINGS_RETRY,
  QA_NAME_LISTING_PROFILE_VIEWINGS_TABLE,
} from './RentalPortalListingProfileViewings.constants'
import { useListingProfileViewings } from './RentalPortalListingProfileViewings.hooks'
import {
  getIsListingViewingsEmpty,
  getListingViewingItemActions,
} from './RentalPortalListingProfileViewings.utils'

import * as styles from './RentalPortalListingProfileViewings.module.css'

import TaskIcon from '../../../../../icons/viewing.svg'

function RentalPortalListingProfileViewings() {
  const { t } = useTranslation()

  const { listingData } = useListingProfileContext()

  const navigate = useNavigate()

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const containerRef = useRef(null)

  function handleCallAction(action, item) {
    navigate(getViewingProfileLink(item.id))
  }

  function handleClickRow(viewingId, event) {
    handleClick({ event, link: getViewingProfileLink(viewingId) })
  }

  function handleMouseDownRow(viewingId, event) {
    handleMouseDown({ event, link: getViewingProfileLink(viewingId) })
  }

  const {
    handleRefresh,
    handleSetPagination,
    hasError,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useListingProfileViewings({ listingId: listingData?.id })

  return (
    <div
      className={styles.RentalPortalListingProfileViewings}
      ref={containerRef}
    >
      {hasError ? (
        <RentalPortalListingProfileTabLoadError
          isLoading={isLoading}
          qaName={QA_NAME_LISTING_PROFILE_VIEWINGS_RETRY}
          textKey='RentalPortal.ListingProfileViewingsLoadErrorKey'
          onRetry={handleRefresh}
        />
      ) : getIsListingViewingsEmpty({ hasError, isInitiated, items }) ? (
        <PageEmptyState
          EntityIcon={TaskIcon}
          text={t('RentalPortal.ListingProfileViewingsEmptyKey')}
        />
      ) : (
        <>
          <Table
            TableRow={ListingProfileViewingsRow}
            actionsColumnWidth={0}
            columns={LISTING_VIEWING_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_LISTING_PROFILE_VIEWINGS_TABLE}
            headerRowProps={{ resizable: false }}
            isLoading={isLoading}
            items={items}
            rowProps={{
              getActions: getListingViewingItemActions,
              onCallAction: handleCallAction,
              onClick: handleClickRow,
              onMouseDown: handleMouseDownRow,
            }}
            skeletonProps={{
              headerRowClassName: styles.skeletonHeaderRow,
              rowClassName: styles.skeletonRow,
              withCheckbox: false,
            }}
            skeletonRows={LISTING_PROFILE_VIEWINGS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      )}
    </div>
  )
}

export default RentalPortalListingProfileViewings
